import { ReactNode } from 'react'

interface DataTableCellProps {
  children: ReactNode
  isSelected?: boolean
  isSticky?: boolean
  isScrolledX?: boolean
  className?: string
  isHeader?: boolean
}

export function DataTableCell({
  children,
  isSelected = false,
  isSticky = false,
  isScrolledX = false,
  className = '',
  isHeader = false
}: DataTableCellProps) {
  const Tag = isHeader ? 'th' : 'td'

  const baseClasses = isHeader
    ? 'px-4 py-1 font-normal bg-table-header sticky top-0 relative'
    : `px-4 py-2 relative ${isSelected ? 'bg-surface-selected' : ''}`

  const stickyClasses = isSticky
    ? isHeader
      ? 'z-50 left-0'
      : `sticky left-0 z-10 transition-colors duration-0 ${isSelected ? 'bg-surface-selected' : 'bg-table group-hover:bg-table-row-hover'}`
    : isHeader
      ? 'z-40'
      : ''

  const borderColor = isSelected ? 'bg-table-grid-selected' : 'bg-table-grid'

  return (
    <Tag className={`${baseClasses} ${stickyClasses} ${className}`}>
      {children}
      <div className="absolute bottom-0 left-0 w-full h-px bg-table-grid" style={{ display: isHeader ? 'block' : 'none' }} />
      <div className={`absolute top-0 right-0 h-full ${borderColor}`} style={{ width: '1px' }} />
      {isSticky && (
        <div
          className={`absolute top-0 -right-4 h-full w-4 pointer-events-none transition-opacity duration-200 ${isScrolledX ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to right, var(--scroll-shadow), transparent)' }}
        />
      )}
    </Tag>
  )
}

interface DataTableHeaderCellProps {
  children: ReactNode
  columnId?: string
  isSticky?: boolean
  isScrolledX?: boolean
  className?: string
  showBorder?: boolean
  resizable?: boolean
  isResizing?: boolean
  onResizeStart?: (e: React.MouseEvent) => void
}

export function DataTableHeaderCell({
  children,
  columnId,
  isSticky = false,
  isScrolledX = false,
  className = '',
  showBorder = true,
  resizable = false,
  isResizing = false,
  onResizeStart
}: DataTableHeaderCellProps) {
  const stickyClasses = isSticky ? 'z-50 left-0' : 'z-40'

  return (
    <th
      className={`px-4 py-1 font-normal bg-table-header sticky top-0 relative ${stickyClasses} ${className}`}
      data-column-id={columnId}
    >
      {children}
      <div className="absolute bottom-0 left-0 w-full h-px bg-table-grid" />
      {showBorder && !resizable && (
        <div className="absolute top-0 right-0 h-full bg-table-grid" style={{ width: '1px' }} />
      )}
      {resizable && (
        <div
          className={`absolute top-0 right-0 h-full w-[1px] ${isResizing ? 'bg-info-solid' : 'bg-divider'} transition-colors`}
          style={{ zIndex: 60 }}
        >
          <div
            className="absolute inset-y-0 -left-[4px] -right-[4px] cursor-col-resize hover:bg-info-solid/20"
            onMouseDown={onResizeStart}
          />
        </div>
      )}
      {isSticky && (
        <div
          className={`absolute top-0 -right-4 h-full w-4 pointer-events-none transition-opacity duration-200 ${isScrolledX ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to right, var(--scroll-shadow), transparent)' }}
        />
      )}
    </th>
  )
}

interface DataTableBodyCellProps {
  children: ReactNode
  isSelected?: boolean
  isSticky?: boolean
  isScrolledX?: boolean
  className?: string
  disableRowHover?: boolean
  /** Draws the row's opening edge. Set on the first cell of a selected row so the
   *  selection closes into a box with the heavier rules above and below it. */
  showLeftEdge?: boolean
}

export function DataTableBodyCell({
  children,
  isSelected = false,
  isSticky = false,
  isScrolledX = false,
  className = '',
  disableRowHover = false,
  showLeftEdge = false
}: DataTableBodyCellProps) {
  // All cells need explicit background handling for consistent hover behavior
  const getBgClass = () => {
    if (isSelected) return 'bg-surface-selected'
    if (disableRowHover) return 'bg-table'
    return 'bg-table group-hover:bg-table-row-hover'
  }

  const bgClass = getBgClass()
  const stickyClass = isSticky ? 'sticky left-0 z-10' : ''
  const borderColor = isSelected ? 'bg-table-grid-selected' : 'bg-table-grid'

  return (
    <td
      className={`px-4 py-2 relative transition-colors duration-0 ${bgClass} ${stickyClass} ${className}`}
    >
      <div className="overflow-visible">{children}</div>
      {showLeftEdge && (
        <div className={`absolute top-0 left-0 h-full ${borderColor}`} style={{ width: '1px' }} />
      )}
      <div className={`absolute top-0 right-0 h-full ${borderColor}`} style={{ width: '1px' }} />
      {isSticky && (
        <div
          className={`absolute top-0 -right-4 h-full w-4 pointer-events-none transition-opacity duration-200 ${isScrolledX ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to right, var(--scroll-shadow), transparent)' }}
        />
      )}
    </td>
  )
}
